import AuthContext from '@context/AuthContext';
import { Amplify } from 'aws-amplify';
import { use, useEffect, useState } from 'react';
import { AurigaAuthUser, AuthProviderProps } from 'types/Auth.types';

import {
  authenticateUser,
  checkAuthentication,
  getAuthenticatedUserWithAttributes,
  handleRedirectAfterAuth,
  setInitialized,
  signOutUser,
} from './auth';
import { checkAuthConfigSettings, configureAmplify } from './settings';

/**
 * AuthProvider Component
 *
 * This component provides authentication context to the entire application.
 * It handles authentication state management, user information, and authentication flows.
 *
 * Key responsibilities:
 * - Initialize authentication configuration from cookies or dev config
 * - Configure AWS Amplify with the appropriate settings
 * - Check and maintain authentication state
 * - Provide authentication methods (signIn, signOut) to child components
 * - Handle authentication errors with appropriate feedback
 *
 * The component uses React Context to make authentication state and methods
 * available throughout the component tree without prop drilling.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AurigaAuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  // For added Error handling and visibility via the AuthContext
  const [error, setError] = useState<string | null>(null);
  const [solution, setSolution] = useState<string | null>(null);

  /**
   * Initialize authentication on component mount
   *
   * This effect runs once when the component mounts and:
   * - Checks if auth configuration is available from cookies or dev config
   * - Configures Amplify with the appropriate settings
   * - Verifies the current authentication state
   * - Retrieves user information if authenticated
   * - Updates the authentication state and loading state
   *
   * Error handling is implemented to provide clear feedback for configuration
   * or authentication issues.
   */
  useEffect(() => {
    // Check if auth config is initialized and user is authenticated on component mount
    const checkInitAndAuth = async () => {
      // First check auth configuration settings and initialize if needed
      const authConfigResult = await checkAuthConfigSettings();

      if (!authConfigResult.isReady) {
        setError(authConfigResult.error || 'Auth config is not ready.');
        setSolution(authConfigResult.solution || 'Check dev-auth-config.ts exists.');
        console.error('Auth config is not ready:', authConfigResult.error);
        console.error('Solution:', authConfigResult.solution);
        setLoading(false);
        return;
      }

      // Configure Amplify if not already configured
      Amplify.configure(await configureAmplify());
      setInitialized(true);

      try {
        // Check current User authentication status
        const isAuth = await checkAuthentication();
        setIsAuthenticated(isAuth);

        if (isAuth) {
          handleRedirectAfterAuth();
          const userData = await getAuthenticatedUserWithAttributes();
          setUser(userData as unknown as AurigaAuthUser);
        }
      } catch (authStatusError) {
        setError('Authentication status error');
        setSolution('Check debug output for details.');
        console.error('Error checking authentication status:', authStatusError);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkInitAndAuth();
  }, []);

  /**
   * Re-validate authentication on any tab visibility change (loss of focus) to address stale sessions
   * Note: Our CDK sets the session timeout to 6 hours, so this helps catch those cases
   *  where a browser was left open and the session expired
   */
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        const stillAuthenticated = await checkAuthentication();
        if (!stillAuthenticated) {
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  /**
   * Handles user sign-in
   *
   * This method initiates the federated authentication flow by:
   * - Storing the current URL for post-authentication redirect
   * - Calling the authenticateUser function which redirects to the identity provider
   * - Updating the authentication state and user information upon successful authentication
   *
   * Note: Due to the redirect nature of federated authentication, this function
   * will not complete execution until after the redirect cycle completes.
   *
   * @returns A promise that resolves when the sign-in process is complete
   * @throws Will throw an error if the sign-in process fails
   */
  const handleSignIn = async () => {
    try {
      await authenticateUser();

      // Note: The code below won't execute immediately as signInWithRedirect redirects the browser
      const userData = await getAuthenticatedUserWithAttributes();
      setUser(userData as unknown as AurigaAuthUser);
      setIsAuthenticated(true);
    } catch (signInError) {
      console.error('Error signing in:', signInError);
      throw signInError;
    }
  };

  /**
   * Handles user sign-out
   *
   * This method signs the user out by:
   * - Calling the signOutUser function to clear authentication tokens
   * - Resetting the user state to null
   * - Setting isAuthenticated to false
   *
   * @returns A promise that resolves when the sign-out process is complete
   * @throws Will throw an error if the sign-out process fails
   */
  const handleSignOut = async () => {
    try {
      await signOutUser();
      setUser(null);
      setIsAuthenticated(false);
    } catch (signOutError) {
      console.error('Error signing out:', signOutError);
      throw signOutError;
    }
  };

  return (
    <AuthContext
      value={{
        isAuthenticated,
        user,
        signIn: handleSignIn,
        signOut: handleSignOut,
        loading,
        error,
        solution,
      }}
    >
      {children}
    </AuthContext>
  );
};

/**
 * Custom hook to access the authentication context
 *
 * This hook provides a convenient way to access authentication state and methods
 * from any component in the application.
 *
 * @returns The authentication context containing isAuthenticated, user, signIn, signOut, etc.
 */
export const useAuth = () => use(AuthContext);
